(() => {
  const ROOT_SELECTOR = '[data-rd-salty-header]';
  const instances = new WeakMap();

  class MirraiSaltyHeader {
    constructor(root) {
      this.root = root;
      this.layer = root.querySelector('[data-drawer-layer]');
      this.drawer = root.querySelector('[data-drawer]');
      this.openButton = root.querySelector('[data-drawer-open]');
      this.genderTrigger = root.querySelector('[data-gender-trigger]');
      this.genderMenu = root.querySelector('[data-gender-menu]');
      this.announcement = root.querySelector('[data-announcement]');
      this.marqueeTrack = this.announcement?.querySelector('[data-marquee-track]') || null;
      this.marqueeSource = this.announcement?.querySelector('[data-marquee-group]') || null;
      this.marqueeTemplate = this.marqueeSource?.innerHTML || '';
      this.marqueeEnabled = this.announcement?.dataset.marqueeEnabled !== 'false';
      this.marqueeResizeFrame = 0;
      this.searchInput = root.querySelector('[data-animated-search-input]');
      this.searchPlaceholderConfigNode = root.querySelector('[data-search-placeholder-config]');
      this.searchPlaceholderTimer = 0;
      this.searchPlaceholderState = null;

      // Miray advanced search add-on
      this.smartSearch = root.querySelector('[data-rd-smart-search]');
      this.searchForm = root.querySelector('[data-rd-search-form]');
      this.searchPanel = root.querySelector('[data-rd-search-panel]');
      this.searchReset = root.querySelector('[data-rd-search-reset]');
      this.searchConfigNode = root.querySelector('[data-rd-smart-search-config]');
      this.searchProducts = root.querySelector('[data-rd-search-products]');
      this.searchEmpty = root.querySelector('[data-rd-search-empty]');
      this.searchViewAll = root.querySelector('[data-rd-search-view-all]');
      this.searchLoading = root.querySelector('[data-rd-search-loading]');
      this.searchStatus = root.querySelector('[data-rd-search-status]');
      this.searchResultCount = root.querySelector('[data-rd-search-result-count]');
      this.searchRecentSection = root.querySelector('[data-rd-recent-section]');
      this.searchRecentList = root.querySelector('[data-rd-recent-list]');
      this.searchCategorySection = root.querySelector('[data-rd-dynamic-categories]');
      this.searchCategoryResults = root.querySelector('[data-rd-category-results]');
      this.searchSuggestionSection = root.querySelector('[data-rd-dynamic-suggestions]');
      this.searchSuggestionResults = root.querySelector('[data-rd-suggestion-results]');
      this.searchDefaultTrending = root.querySelector('[data-rd-default-trending]');
      this.searchDefaultCollections = root.querySelector('[data-rd-default-collections]');
      this.searchConfig = this.readSmartSearchConfig();
      this.searchDefaultProductsHTML = this.searchProducts?.innerHTML || '';
      this.searchOpen = false;
      this.searchDebounceTimer = 0;
      this.searchRequestController = null;
      this.searchSubmitController = null;
      this.searchCache = new Map();
      this.searchCollectionCandidates = null;
      this.prefetchedCollectionUrls = new Set();

      this.sectionWrapper = root.closest('.section-header');
      this.currentAudience = 'women';
      this.lastFocused = null;
      this.lastScrollY = window.scrollY;
      this.ticking = false;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.resizeObserver = null;
      this.init();
    }

    init() {
      document.body.classList.add('rd-salty-header-present');
      this.root.dataset.rdReady = 'true';

      const rootStyles = getComputedStyle(this.root);
      document.body.style.setProperty('--rd-salty-accent', rootStyles.getPropertyValue('--rd-purple').trim() || '#3F1016');
      document.body.style.setProperty('--rd-salty-soft', rootStyles.getPropertyValue('--rd-purple-soft').trim() || '#F5E8D2');
      document.body.style.setProperty('--rd-salty-gold', rootStyles.getPropertyValue('--rd-gold').trim() || '#DCBE8E');
      this.setupAnnouncementMarquee();
      this.setupAnimatedSearchPlaceholder();
      this.setupSmartSearch();
      if (document.fonts?.ready) document.fonts.ready.then(() => this.scheduleAnnouncementRefresh());

      if (this.root.dataset.stickyMode !== 'none' && this.sectionWrapper) {
        this.sectionWrapper.classList.add('rd-salty-section-sticky');
        this.updateStickyOffset();
        if ('ResizeObserver' in window && this.announcement) {
          this.resizeObserver = new ResizeObserver(() => this.updateStickyOffset());
          this.resizeObserver.observe(this.announcement);
        }
      }

      this.openButton?.addEventListener('click', () => this.openDrawer(this.currentAudience), { signal: this.signal });
      this.root.querySelectorAll('[data-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer(), { signal: this.signal });
      });
      this.root.querySelectorAll('[data-drawer-tab]').forEach((button) => {
        button.addEventListener('click', () => this.activateAudience(button.dataset.drawerTab, true), { signal: this.signal });
      });
      this.root.querySelectorAll('[data-panel-trigger]').forEach((button) => {
        button.addEventListener('click', () => this.openPanel(button), { signal: this.signal });
      });
      this.genderTrigger?.addEventListener('click', () => this.toggleGenderMenu(), { signal: this.signal });
      this.root.querySelectorAll('[data-gender-choice]').forEach((button) => {
        button.addEventListener('click', () => this.chooseGender(button.dataset.genderChoice, button.textContent.trim()), { signal: this.signal });
      });
      this.root.querySelectorAll('[data-rd-cart-drawer-open]').forEach((button) => {
        button.addEventListener('click', (event) => this.openCartDrawer(event, button), { signal: this.signal });
        button.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() !== 'SPACE') return;
          this.openCartDrawer(event, button);
        }, { signal: this.signal });
      });

      document.addEventListener('keydown', (event) => this.onKeydown(event), { signal: this.signal });
      document.addEventListener('click', (event) => this.onDocumentClick(event), { signal: this.signal });
      window.addEventListener('scroll', () => this.onScroll(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.handleResize(), { passive: true, signal: this.signal });
    }

    readSmartSearchConfig() {
      if (!this.searchConfigNode) return {};
      try {
        return JSON.parse(this.searchConfigNode.textContent || '{}');
      } catch (error) {
        console.warn('RD header: invalid advanced search settings.', error);
        return {};
      }
    }

    setupSmartSearch() {
      if (!this.smartSearch || !this.searchInput || !this.searchPanel || this.searchConfig.enabled === false) return;

      this.renderRecentSearches();
      this.toggleSearchReset();

      this.searchInput.addEventListener('focus', () => this.openSmartSearch(), { signal: this.signal });
      this.searchInput.addEventListener('click', () => this.openSmartSearch(), { signal: this.signal });
      this.searchInput.addEventListener('input', () => this.handleSmartSearchInput(), { signal: this.signal });
      this.searchInput.addEventListener('search', () => this.handleSmartSearchInput(), { signal: this.signal });
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.isComposing) return;
        event.preventDefault();
        event.stopPropagation();
        this.handleCollectionSearchSubmit(event);
      }, { capture: true, signal: this.signal });

      this.searchForm?.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.handleCollectionSearchSubmit(event);
      }, { capture: true, signal: this.signal });

      this.searchReset?.addEventListener('click', () => {
        this.searchInput.value = '';
        this.toggleSearchReset();
        this.cancelSmartSearchRequest();
        this.showDefaultSearchState();
        this.openSmartSearch();
        this.searchInput.focus();
      }, { signal: this.signal });

      this.smartSearch.addEventListener('click', (event) => this.onSmartSearchClick(event), { signal: this.signal });
    }

    openSmartSearch() {
      if (!this.searchPanel || this.searchConfig.enabled === false) return;

      this.searchOpen = true;
      this.searchPanel.hidden = false;
      this.smartSearch?.classList.add('is-open');
      this.root.classList.add('search-is-open');
      document.body.classList.add('rd-smart-search-open');
      this.searchInput?.setAttribute('aria-expanded', 'true');

      const query = this.normaliseSearchTerm(this.searchInput?.value);
      if (query) this.queueSmartSearch(query, 0);
      else this.showDefaultSearchState();
    }

    closeSmartSearch(restoreFocus = false) {
      if (!this.searchPanel) return;

      this.searchOpen = false;
      this.searchPanel.hidden = true;
      this.smartSearch?.classList.remove('is-open');
      this.root.classList.remove('search-is-open');
      document.body.classList.remove('rd-smart-search-open');
      this.searchInput?.setAttribute('aria-expanded', 'false');
      this.setSmartSearchLoading(false);
      this.cancelSmartSearchRequest();

      if (restoreFocus) this.searchInput?.focus();
    }

    handleSmartSearchInput() {
      if (!this.searchInput) return;

      this.toggleSearchReset();
      const query = this.normaliseSearchTerm(this.searchInput.value);
      this.openSmartSearch();

      if (!query) {
        this.cancelSmartSearchRequest();
        this.showDefaultSearchState();
        return;
      }

      this.prefetchBestLocalCollection(query);
      this.queueSmartSearch(query, 120);
    }

    queueSmartSearch(query, delay = 220) {
      window.clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = window.setTimeout(() => this.fetchSmartSearch(query), Math.max(0, delay));
    }

    cancelSmartSearchRequest() {
      window.clearTimeout(this.searchDebounceTimer);
      this.searchRequestController?.abort();
      this.searchRequestController = null;
    }

    async fetchSmartSearch(query) {
      if (!query || !this.searchConfig.endpoint) return;

      const cacheKey = query.toLocaleLowerCase();
      if (this.searchCache.has(cacheKey)) {
        this.renderSmartSearchResults(query, this.searchCache.get(cacheKey));
        return;
      }

      this.searchRequestController?.abort();
      const controller = new AbortController();
      this.searchRequestController = controller;
      this.setSmartSearchLoading(true);
      this.setSearchStatus(`Searching for ${query}`);

      try {
        const url = new URL(this.searchConfig.endpoint, window.location.origin);
        const maximumLimit = Math.max(
          Number(this.searchConfig.productLimit) || 8,
          Number(this.searchConfig.suggestionLimit) || 8
        );

        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', String(maximumLimit));
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Search request failed with status ${response.status}`);

        const payload = await response.json();
        const results = payload?.resources?.results || {};
        this.searchCache.set(cacheKey, results);
        this.prefetchBestCollection(query, results);

        if (this.normaliseSearchTerm(this.searchInput?.value).toLocaleLowerCase() !== cacheKey) return;
        this.renderSmartSearchResults(query, results);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.warn('RD header: advanced search request failed.', error);
        this.renderSmartSearchResults(query, {});
      } finally {
        if (this.searchRequestController === controller) {
          this.searchRequestController = null;
          this.setSmartSearchLoading(false);
        }
      }
    }

    renderSmartSearchResults(query, results) {
      const productLimit = Number(this.searchConfig.productLimit) || 8;
      const products = this.sortProductsForQuery(Array.isArray(results.products) ? results.products : [], query)
        .slice(0, productLimit);
      const collections = (Array.isArray(results.collections) ? results.collections : [])
        .slice(0, Number(this.searchConfig.suggestionLimit) || 8);
      const suggestions = this.buildSearchSuggestions(query, results, products);

      if (this.searchDefaultTrending) this.searchDefaultTrending.hidden = true;
      if (this.searchDefaultCollections) this.searchDefaultCollections.hidden = true;
      if (this.searchRecentSection) this.searchRecentSection.hidden = true;

      this.renderCategoryResults(collections);
      this.renderSuggestionResults(suggestions);
      this.renderProductResults(products);

      if (this.searchViewAll) {
        const bestCollection = this.findBestMatchingCollection(query, results || {});
        this.searchViewAll.href = bestCollection?.url || this.getFallbackCollectionUrl();
        this.searchViewAll.hidden = false;
      }

      if (this.searchResultCount) {
        this.searchResultCount.textContent = products.length ? `${products.length} result${products.length === 1 ? '' : 's'}` : '';
      }

      this.setSearchStatus(products.length
        ? `${products.length} products found for ${query}`
        : `No products found for ${query}`);
    }

    showDefaultSearchState() {
      if (this.searchDefaultTrending) this.searchDefaultTrending.hidden = false;
      if (this.searchDefaultCollections) this.searchDefaultCollections.hidden = false;
      if (this.searchCategorySection) this.searchCategorySection.hidden = true;
      if (this.searchSuggestionSection) this.searchSuggestionSection.hidden = true;
      if (this.searchCategoryResults) this.searchCategoryResults.innerHTML = '';
      if (this.searchSuggestionResults) this.searchSuggestionResults.innerHTML = '';
      if (this.searchProducts) this.searchProducts.innerHTML = this.searchDefaultProductsHTML;
      if (this.searchEmpty) this.searchEmpty.hidden = true;
      if (this.searchViewAll) this.searchViewAll.hidden = true;
      if (this.searchResultCount) this.searchResultCount.textContent = '';
      this.renderRecentSearches();
      this.setSearchStatus('Search suggestions opened');
    }

    renderCategoryResults(collections) {
      if (!this.searchCategorySection || !this.searchCategoryResults) return;

      if (!collections.length) {
        this.searchCategorySection.hidden = true;
        this.searchCategoryResults.innerHTML = '';
        return;
      }

      this.searchCategoryResults.innerHTML = collections.map((collection) => `
        <li>
          <a class="rd-miray-search-text-link" href="${this.escapeHTML(collection.url || '#')}" data-rd-search-result-link>
            ${this.escapeHTML(collection.title || '')}
          </a>
        </li>
      `).join('');
      this.searchCategorySection.hidden = false;
    }

    renderSuggestionResults(suggestions) {
      if (!this.searchSuggestionSection || !this.searchSuggestionResults) return;

      if (!suggestions.length) {
        this.searchSuggestionSection.hidden = true;
        this.searchSuggestionResults.innerHTML = '';
        return;
      }

      this.searchSuggestionResults.innerHTML = suggestions.map((term) => `
        <li>
          <button type="button" class="rd-miray-search-term" data-rd-search-term="${this.escapeHTML(term)}">
            <span>${this.escapeHTML(term)}</span>
          </button>
        </li>
      `).join('');
      this.searchSuggestionSection.hidden = false;
    }

    renderProductResults(products) {
      if (!this.searchProducts) return;

      this.searchProducts.innerHTML = products.map((product) => this.renderSearchProductCard(product)).join('');
      if (this.searchEmpty) this.searchEmpty.hidden = products.length > 0;
    }

    renderSearchProductCard(product) {
      const title = String(product?.title || 'Product');
      const url = product?.url || '#';
      const imageObject = product?.featured_image || product?.image || null;
      const imageUrl = typeof imageObject === 'string' ? imageObject : imageObject?.url;
      const imageAlt = typeof imageObject === 'object' && imageObject?.alt ? imageObject.alt : title;
      const price = this.toNumber(product?.price ?? product?.price_min);
      const compareAtPrice = this.toNumber(product?.compare_at_price_max ?? product?.compare_at_price);
      const hasDiscount = compareAtPrice > price && price >= 0;
      const discount = hasDiscount ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;
      const media = imageUrl
        ? `<img class="rd-miray-product-card__image" src="${this.escapeHTML(this.getSizedImageUrl(imageUrl, 520))}" alt="${this.escapeHTML(imageAlt)}" loading="lazy" width="520" height="520">`
        : '<svg class="rd-miray-product-card__placeholder" viewBox="0 0 120 120" aria-hidden="true"><path d="M60 19 68 48 97 60 68 72 60 101 52 72 23 60 52 48Z" fill="currentColor" opacity=".18"/></svg>';

      return `
        <a class="rd-miray-product-card" href="${this.escapeHTML(url)}" data-rd-search-product-link>
          <div class="rd-miray-product-card__media">${media}</div>
          <div class="rd-miray-product-card__content">
            <h4 class="rd-miray-product-card__title">${this.escapeHTML(title)}</h4>
            <div class="rd-miray-product-card__price-row">
              <span class="rd-miray-product-card__price">${this.escapeHTML(this.formatSearchMoney(price))}</span>
              ${hasDiscount ? `<s>${this.escapeHTML(this.formatSearchMoney(compareAtPrice))}</s>` : ''}
              ${discount > 0 ? `<span class="rd-miray-product-card__discount">${discount}% off</span>` : ''}
            </div>
          </div>
        </a>
      `;
    }

    sortProductsForQuery(products, query) {
      const needle = query.toLocaleLowerCase();
      return [...products].sort((a, b) => {
        const aTitle = String(a?.title || '').toLocaleLowerCase();
        const bTitle = String(b?.title || '').toLocaleLowerCase();
        const score = (title) => {
          if (title.startsWith(needle)) return 0;
          if (title.split(/\s+/).some((word) => word.startsWith(needle))) return 1;
          if (title.includes(needle)) return 2;
          return 3;
        };
        return score(aTitle) - score(bTitle);
      });
    }

    buildSearchSuggestions(query, results, products) {
      const queryObjects = Array.isArray(results.queries) ? results.queries : [];
      const candidates = [
        ...products.map((product) => product?.title),
        ...queryObjects.map((item) => item?.text || item?.styled_text)
      ]
        .map((value) => this.stripHTML(value))
        .map((value) => this.normaliseSearchTerm(value))
        .filter(Boolean);

      const needle = query.toLocaleLowerCase();
      const unique = [];
      const seen = new Set();

      candidates
        .sort((a, b) => {
          const aTitle = a.toLocaleLowerCase();
          const bTitle = b.toLocaleLowerCase();
          const aStarts = aTitle.startsWith(needle) || aTitle.split(/\s+/).some((word) => word.startsWith(needle));
          const bStarts = bTitle.startsWith(needle) || bTitle.split(/\s+/).some((word) => word.startsWith(needle));
          return Number(bStarts) - Number(aStarts);
        })
        .forEach((candidate) => {
          const key = candidate.toLocaleLowerCase();
          if (seen.has(key)) return;
          seen.add(key);
          unique.push(candidate);
        });

      return unique.slice(0, Number(this.searchConfig.suggestionLimit) || 8);
    }

    onSmartSearchClick(event) {
      const clearButton = event.target.closest('[data-rd-clear-recent]');
      if (clearButton) {
        event.preventDefault();
        this.clearRecentSearches();
        return;
      }

      const removeButton = event.target.closest('[data-rd-remove-recent]');
      if (removeButton) {
        event.preventDefault();
        event.stopPropagation();
        this.removeRecentSearch(removeButton.dataset.rdRemoveRecent || '');
        return;
      }

      const termButton = event.target.closest('[data-rd-search-term], [data-rd-recent-term]');
      if (termButton) {
        event.preventDefault();
        event.stopPropagation();

        const term = termButton.dataset.rdSearchTerm || termButton.dataset.rdRecentTerm || '';
        const collectionUrl = termButton.dataset.rdCollectionUrl || '';

        this.rememberSearch(term);

        if (collectionUrl) {
          this.navigateToCollection(collectionUrl);
          return;
        }

        if (this.searchInput) {
          this.searchInput.value = term;
          this.toggleSearchReset();
        }

        this.handleCollectionSearchSubmit(event);
        return;
      }

      const resultLink = event.target.closest('[data-rd-search-product-link], [data-rd-search-result-link], [data-rd-search-view-all]');
      if (resultLink) this.rememberSearch(this.searchInput?.value);
    }

    renderRecentSearches() {
      if (!this.searchRecentSection || !this.searchRecentList) return;

      const items = this.searchConfig.showRecent === false ? [] : this.readRecentSearches();
      if (!items.length || this.normaliseSearchTerm(this.searchInput?.value)) {
        this.searchRecentSection.hidden = true;
        this.searchRecentList.innerHTML = '';
        return;
      }

      this.searchRecentList.innerHTML = items.map((term) => `
        <li class="rd-miray-search-recent-item">
          <button type="button" class="rd-miray-search-recent-link" data-rd-recent-term="${this.escapeHTML(term)}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2"/></svg>
            <span>${this.escapeHTML(term)}</span>
          </button>
          <button type="button" class="rd-miray-search-remove-recent" data-rd-remove-recent="${this.escapeHTML(term)}" aria-label="Remove ${this.escapeHTML(term)} from recent searches">&times;</button>
        </li>
      `).join('');
      this.searchRecentSection.hidden = false;
    }

    readRecentSearches() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.searchConfig.storageKey || 'rd-miray-recent-searches') || '[]');
        return Array.isArray(stored) ? stored.filter((item) => typeof item === 'string' && item.trim()) : [];
      } catch (error) {
        return [];
      }
    }

    writeRecentSearches(items) {
      try {
        localStorage.setItem(this.searchConfig.storageKey || 'rd-miray-recent-searches', JSON.stringify(items));
      } catch (error) {
        // Local storage may be disabled. Search still works normally.
      }
    }

    rememberSearch(value) {
      const term = this.normaliseSearchTerm(value).slice(0, 100);
      if (!term) return;

      const limit = Number(this.searchConfig.recentLimit) || 5;
      const existing = this.readRecentSearches().filter((item) => item.toLocaleLowerCase() !== term.toLocaleLowerCase());
      this.writeRecentSearches([term, ...existing].slice(0, limit));
    }

    removeRecentSearch(term) {
      const needle = this.normaliseSearchTerm(term).toLocaleLowerCase();
      const items = this.readRecentSearches().filter((item) => item.toLocaleLowerCase() !== needle);
      this.writeRecentSearches(items);
      this.renderRecentSearches();
    }

    clearRecentSearches() {
      this.writeRecentSearches([]);
      this.renderRecentSearches();
    }

    async handleCollectionSearchSubmit(event) {
      event?.preventDefault?.();
      event?.stopPropagation?.();

      const query = this.normaliseSearchTerm(this.searchInput?.value);
      if (!query) {
        this.searchInput?.focus();
        return;
      }

      this.rememberSearch(query);
      this.searchSubmitController?.abort();

      // 1. Instant path: match against collections already rendered in the header.
      // This handles Watches, Jewellery, Scarves, Shades, Gifts, Shoes->Accessories, etc.
      const localCollection = this.findBestMatchingCollection(query, {});
      if (localCollection?.url) {
        this.navigateToCollection(localCollection.url);
        return;
      }

      // 2. Instant path: use predictive results that were fetched while the user typed.
      const cacheKey = query.toLocaleLowerCase();
      const cachedResults = this.searchCache.get(cacheKey);
      if (cachedResults) {
        const cachedCollection = this.findBestMatchingCollection(query, cachedResults);
        if (cachedCollection?.url) {
          this.navigateToCollection(cachedCollection.url);
          return;
        }

        this.navigateToCollection(this.getFallbackCollectionUrl());
        return;
      }

      // 3. Only when no local/cache match exists, make one small request.
      // It is capped so the visitor never waits several seconds.
      const controller = new AbortController();
      this.searchSubmitController = controller;
      const timeoutId = window.setTimeout(() => controller.abort(), 650);

      this.setSearchStatus(`Opening the best collection for ${query}`);

      try {
        const results = await this.fetchSearchResultsForCollectionRedirect(query, controller.signal);
        this.searchCache.set(cacheKey, results || {});

        const collection = this.findBestMatchingCollection(query, results || {});
        this.navigateToCollection(collection?.url || this.getFallbackCollectionUrl());
        return;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('RD header: fast collection lookup failed; opening fallback collection.', error);
        }
      } finally {
        window.clearTimeout(timeoutId);
        if (this.searchSubmitController === controller) {
          this.searchSubmitController = null;
        }
      }

      this.navigateToCollection(this.getFallbackCollectionUrl());
    }

    navigateToCollection(url) {
      const destination = String(url || this.getFallbackCollectionUrl()).trim();
      if (!destination) return;

      this.setSearchStatus('Opening collection');
      this.searchForm?.setAttribute('aria-busy', 'true');
      window.location.assign(destination);
    }

    async fetchSearchResultsForCollectionRedirect(query, signal) {
      if (!this.searchConfig.endpoint) return {};

      const url = new URL(this.searchConfig.endpoint, window.location.origin);
      const maximumLimit = 5;

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection');
      url.searchParams.set('resources[limit]', String(maximumLimit));
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set('resources[options][fields]', 'title,product_type');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal
      });

      if (!response.ok) {
        throw new Error(`Collection redirect request failed with status ${response.status}`);
      }

      const payload = await response.json();
      return payload?.resources?.results || {};
    }

    findBestMatchingCollection(query, results) {
      const predictiveCollections = Array.isArray(results?.collections) ? results.collections : [];
      const products = Array.isArray(results?.products) ? results.products : [];
      const configuredCollections = this.getConfiguredCollectionCandidates();

      const candidates = [];
      const seenUrls = new Set();

      const addCandidate = (collection, source, sourceIndex = 0) => {
        const url = String(collection?.url || '').trim();
        if (!url || seenUrls.has(url)) return;

        seenUrls.add(url);
        candidates.push({
          title: String(collection?.title || '').trim(),
          url,
          source,
          sourceIndex
        });
      };

      predictiveCollections.forEach((collection, index) => {
        addCandidate(collection, 'predictive', index);
      });

      configuredCollections.forEach((collection, index) => {
        addCandidate(collection, 'configured', index);
      });

      if (!candidates.length) return null;

      const queryKey = this.createSearchMatchKey(query);
      const queryTokens = this.createSearchMatchTokens(query);
      const relatedKeys = this.getRelatedSearchKeys(query);
      const productTypeKeys = products
        .map((product) => this.createSearchMatchKey(product?.product_type || ''))
        .filter(Boolean);

      const scored = candidates.map((collection) => {
        const titleKey = this.createSearchMatchKey(collection.title);
        const titleTokens = this.createSearchMatchTokens(collection.title);
        const handleKey = this.createSearchMatchKey(this.getCollectionHandle(collection.url));

        let score = Number.POSITIVE_INFINITY;

        if (queryKey && (queryKey === titleKey || queryKey === handleKey)) {
          score = 0;
        } else if (this.haveSameSearchTokens(queryTokens, titleTokens)) {
          score = 1;
        } else if (
          queryKey &&
          (
            titleKey.startsWith(queryKey) ||
            handleKey.startsWith(queryKey) ||
            queryKey.startsWith(titleKey) ||
            queryKey.startsWith(handleKey)
          )
        ) {
          score = 2;
        } else if (
          queryKey &&
          (
            titleKey.includes(queryKey) ||
            handleKey.includes(queryKey) ||
            queryKey.includes(titleKey) ||
            queryKey.includes(handleKey)
          )
        ) {
          score = 3;
        } else if (
          queryTokens.some((token) => titleTokens.includes(token)) ||
          queryTokens.some((token) => handleKey.split(' ').includes(token))
        ) {
          score = 4;
        } else if (
          relatedKeys.some((relatedKey) =>
            relatedKey &&
            (
              titleKey === relatedKey ||
              handleKey === relatedKey ||
              titleKey.includes(relatedKey) ||
              handleKey.includes(relatedKey) ||
              relatedKey.includes(titleKey) ||
              relatedKey.includes(handleKey)
            )
          )
        ) {
          score = 5;
        } else if (
          productTypeKeys.some((productType) =>
            productType &&
            (
              productType === titleKey ||
              productType === handleKey ||
              titleKey.includes(productType) ||
              handleKey.includes(productType) ||
              productType.includes(titleKey) ||
              productType.includes(handleKey)
            )
          )
        ) {
          score = 6;
        } else if (collection.source === 'predictive') {
          // Shopify only returns predictive collections it considers relevant.
          score = 10 + collection.sourceIndex;
        }

        return { collection, score };
      });

      scored.sort((a, b) => a.score - b.score);
      const best = scored[0];

      if (!best || !Number.isFinite(best.score)) return null;
      return best.collection;
    }

    getConfiguredCollectionCandidates() {
      if (Array.isArray(this.searchCollectionCandidates)) {
        return this.searchCollectionCandidates;
      }

      const candidates = [];
      const seenUrls = new Set();

      const configuredTrending = Array.isArray(this.searchConfig.trendingCollections)
        ? this.searchConfig.trendingCollections
        : [];

      configuredTrending.forEach((collection) => {
        const url = String(collection?.url || '').trim();
        const title = this.normaliseSearchTerm(collection?.title || '');
        if (!url || !title || seenUrls.has(url)) return;
        seenUrls.add(url);
        candidates.push({ title, url });
      });

      this.root.querySelectorAll('a[href*="/collections/"]').forEach((link) => {
        const url = link.getAttribute('href') || '';
        const title = this.normaliseSearchTerm(
          link.getAttribute('data-collection-title') ||
          link.querySelector('.rd-miray-category-card__title, .rd-salty-category-card__title, span')?.textContent ||
          link.textContent
        );

        if (!url || !title || seenUrls.has(url)) return;
        seenUrls.add(url);
        candidates.push({ title, url });
      });

      this.searchCollectionCandidates = candidates;
      return candidates;
    }

    prefetchBestLocalCollection(query) {
      const collection = this.findBestMatchingCollection(query, {});
      if (collection?.url) this.prefetchCollectionUrl(collection.url);
    }

    prefetchBestCollection(query, results) {
      const collection = this.findBestMatchingCollection(query, results || {});
      if (collection?.url) this.prefetchCollectionUrl(collection.url);
    }

    prefetchCollectionUrl(urlValue) {
      const url = String(urlValue || '').trim();
      if (!url || this.prefetchedCollectionUrls.has(url)) return;

      this.prefetchedCollectionUrls.add(url);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    }

    getRelatedSearchKeys(query) {
      const tokens = new Set(this.createSearchMatchTokens(query));
      const phrases = new Set();

      const add = (...values) => {
        values.forEach((value) => {
          const key = this.createSearchMatchKey(value);
          if (key) phrases.add(key);
        });
      };

      const groups = [
        {
          match: ['shoe', 'footwear', 'sandal', 'heel', 'sneaker', 'boot', 'slipper'],
          related: ['footwear', 'fashion accessories', 'accessories', 'new arrivals']
        },
        {
          match: ['watch', 'wristwatch', 'timepiece'],
          related: ['watch', 'watches']
        },
        {
          match: ['sunglass', 'shade', 'eyewear', 'glass'],
          related: ['shades', 'sunglasses', 'eyewear']
        },
        {
          match: ['scarf', 'scarves', 'stole', 'bandana'],
          related: ['scarves', 'scarf']
        },
        {
          match: ['jewel', 'jewellery', 'jewelry', 'necklace', 'earring', 'ring', 'bracelet', 'pendant', 'chain'],
          related: ['jewellery', 'jewelry', 'fashion jewellery', 'accessories']
        },
        {
          match: ['gift', 'hamper', 'box'],
          related: ['gift hamper', 'gift box', 'gifts']
        },
        {
          match: ['bag', 'purse', 'clutch', 'wallet'],
          related: ['bags', 'fashion accessories', 'accessories']
        }
      ];

      groups.forEach((group) => {
        if (group.match.some((word) => tokens.has(this.singulariseSearchWord(word)))) {
          add(...group.related);
        }
      });

      return [...phrases];
    }

    getFallbackCollectionUrl() {
      const configured = String(this.searchConfig.fallbackCollectionUrl || '').trim();
      if (configured) return configured;

      const allProducts = String(this.searchConfig.allProductsUrl || '').trim();
      if (allProducts) return allProducts;

      return '/collections/all';
    }

    createSearchMatchKey(value) {
      return this.createSearchMatchTokens(value).join(' ');
    }

    createSearchMatchTokens(value) {
      return String(value || '')
        .toLocaleLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => this.singulariseSearchWord(word));
    }

    singulariseSearchWord(word) {
      if (word.length > 4 && /(?:ches|shes|xes|zes)$/.test(word)) return word.slice(0, -2);
      if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
      if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
      return word;
    }

    haveSameSearchTokens(first, second) {
      if (!first.length || !second.length || first.length !== second.length) return false;
      return first.every((token, index) => token === second[index]);
    }

    getCollectionHandle(urlValue) {
      try {
        const url = new URL(urlValue || '', window.location.origin);
        const parts = url.pathname.split('/').filter(Boolean);
        const collectionIndex = parts.indexOf('collections');
        return collectionIndex >= 0 ? parts[collectionIndex + 1] || '' : '';
      } catch (error) {
        return '';
      }
    }

    buildSearchUrl(term) {
      const url = new URL(this.searchConfig.searchUrl || '/search', window.location.origin);
      url.searchParams.set('q', this.normaliseSearchTerm(term));
      url.searchParams.set('options[prefix]', 'last');
      return `${url.pathname}${url.search}${url.hash}`;
    }

    toggleSearchReset() {
      if (!this.searchReset) return;
      this.searchReset.classList.toggle('hidden', !this.normaliseSearchTerm(this.searchInput?.value));
    }

    setSmartSearchLoading(loading) {
      if (this.searchLoading) this.searchLoading.hidden = !loading;
      this.smartSearch?.classList.toggle('is-loading', Boolean(loading));
    }

    setSearchStatus(message) {
      if (this.searchStatus) this.searchStatus.textContent = message || '';
    }

    normaliseSearchTerm(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    stripHTML(value) {
      return String(value || '').replace(/<[^>]*>/g, '');
    }

    escapeHTML(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    toNumber(value) {
      const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    }

    formatSearchMoney(value) {
      const amount = Number(value) || 0;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.searchConfig.currency || 'INR',
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        return `${this.searchConfig.currency || ''} ${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`.trim();
      }
    }

    getSizedImageUrl(value, width) {
      const url = String(value || '');
      if (!url || /[?&]width=/.test(url)) return url;
      return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
    }

    readSearchPlaceholderConfig() {
      if (!this.searchPlaceholderConfigNode) return {};
      try {
        return JSON.parse(this.searchPlaceholderConfigNode.textContent || '{}');
      } catch (error) {
        console.warn('RD header: invalid animated search placeholder settings.', error);
        return {};
      }
    }

    setupAnimatedSearchPlaceholder() {
      if (!this.searchInput) return;

      const config = this.readSearchPlaceholderConfig();
      const fallback = String(config.fallback || this.searchInput.placeholder || 'Search').trim();
      const rawTerms = typeof config.terms === 'string' ? config.terms : '';
      const terms = rawTerms
        .split(/\r?\n|,/)
        .map((term) => term.trim())
        .filter(Boolean);

      if (!config.enabled || !terms.length) {
        this.searchInput.placeholder = fallback;
        return;
      }

      let prefix = typeof config.prefix === 'string' ? config.prefix.trim() : 'Search for';
      if (prefix && !/\s$/.test(prefix)) prefix += ' ';

      const reducedMotion = Boolean(config.respectReducedMotion)
        && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion) {
        this.searchInput.placeholder = `${prefix}${terms[0]}`;
        return;
      }

      this.searchPlaceholderState = {
        prefix,
        terms,
        wordIndex: 0,
        characterIndex: 0,
        deleting: false,
        paused: false,
        stopped: false,
        typingSpeed: this.normaliseDelay(config.typingSpeed, 90, 20, 1000),
        deletingSpeed: this.normaliseDelay(config.deletingSpeed, 50, 20, 1000),
        holdTime: this.normaliseDelay(config.holdTime, 1400, 0, 10000),
        wordGap: this.normaliseDelay(config.wordGap, 250, 0, 5000),
        startDelay: this.normaliseDelay(config.startDelay, 400, 0, 5000),
        loop: config.loop !== false,
        pauseOnFocus: config.pauseOnFocus !== false
      };

      this.searchInput.placeholder = prefix || fallback;

      if (this.searchPlaceholderState.pauseOnFocus) {
        this.searchInput.addEventListener('focus', () => this.pauseSearchPlaceholder(), { signal: this.signal });
        this.searchInput.addEventListener('blur', () => this.resumeSearchPlaceholder(180), { signal: this.signal });
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseSearchPlaceholder();
        else this.resumeSearchPlaceholder(180);
      }, { signal: this.signal });

      this.scheduleSearchPlaceholder(this.searchPlaceholderState.startDelay);
    }

    normaliseDelay(value, fallback, minimum, maximum) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(Math.max(parsed, minimum), maximum);
    }

    scheduleSearchPlaceholder(delay) {
      window.clearTimeout(this.searchPlaceholderTimer);
      if (!this.searchPlaceholderState || this.searchPlaceholderState.paused || this.searchPlaceholderState.stopped) return;
      this.searchPlaceholderTimer = window.setTimeout(() => this.stepSearchPlaceholder(), Math.max(0, delay));
    }

    pauseSearchPlaceholder() {
      if (!this.searchPlaceholderState) return;
      this.searchPlaceholderState.paused = true;
      window.clearTimeout(this.searchPlaceholderTimer);
    }

    resumeSearchPlaceholder(delay = 0) {
      if (!this.searchPlaceholderState || this.searchPlaceholderState.stopped) return;
      if (this.searchPlaceholderState.pauseOnFocus && document.activeElement === this.searchInput) return;
      this.searchPlaceholderState.paused = false;
      this.scheduleSearchPlaceholder(delay);
    }

    stepSearchPlaceholder() {
      const state = this.searchPlaceholderState;
      if (!state || state.paused || state.stopped || !this.searchInput) return;

      const word = state.terms[state.wordIndex] || '';

      if (!state.deleting) {
        if (state.characterIndex < word.length) {
          state.characterIndex += 1;
          this.searchInput.placeholder = `${state.prefix}${word.slice(0, state.characterIndex)}`;
          this.scheduleSearchPlaceholder(state.typingSpeed);
          return;
        }

        if (!state.loop && state.wordIndex === state.terms.length - 1) {
          state.stopped = true;
          return;
        }

        state.deleting = true;
        this.scheduleSearchPlaceholder(state.holdTime);
        return;
      }

      if (state.characterIndex > 0) {
        state.characterIndex -= 1;
        this.searchInput.placeholder = `${state.prefix}${word.slice(0, state.characterIndex)}`;
        this.scheduleSearchPlaceholder(state.deletingSpeed);
        return;
      }

      state.deleting = false;
      state.wordIndex = (state.wordIndex + 1) % state.terms.length;
      this.scheduleSearchPlaceholder(state.wordGap);
    }

    scrubMarqueeClone(node) {
      if (!(node instanceof Element)) return;
      node.setAttribute('aria-hidden', 'true');
      node.removeAttribute('data-shopify-editor-block');
      node.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
    }

    setupAnnouncementMarquee() {
      if (!this.announcement || !this.marqueeTrack || !this.marqueeSource || !this.marqueeTemplate) return;

      this.marqueeTrack.classList.remove('is-ready');
      this.marqueeTrack.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      this.marqueeSource.innerHTML = this.marqueeTemplate;
      this.marqueeTrack.style.removeProperty('--rd-marquee-distance');
      this.marqueeTrack.style.removeProperty('--rd-marquee-duration');

      if (!this.marqueeEnabled) return;

      const originalItems = [...this.marqueeSource.children];
      if (!originalItems.length) return;

      const minimumWidth = Math.max(this.announcement.clientWidth + 96, 480);
      let safety = 0;
      while (this.marqueeSource.scrollWidth < minimumWidth && safety < 24) {
        originalItems.forEach((item) => {
          const clone = item.cloneNode(true);
          this.scrubMarqueeClone(clone);
          this.marqueeSource.appendChild(clone);
        });
        safety += 1;
      }

      const duplicateGroup = this.marqueeSource.cloneNode(true);
      duplicateGroup.dataset.marqueeClone = 'true';
      this.scrubMarqueeClone(duplicateGroup);
      this.marqueeTrack.appendChild(duplicateGroup);

      const distance = Math.ceil(this.marqueeSource.getBoundingClientRect().width);
      if (!distance) return;
      this.marqueeTrack.style.setProperty('--rd-marquee-distance', `${distance}px`);
      this.marqueeTrack.style.setProperty('--rd-marquee-duration', getComputedStyle(this.root).getPropertyValue('--rd-announcement-speed').trim() || '24s');

      void this.marqueeTrack.offsetWidth;
      this.marqueeTrack.classList.add('is-ready');
    }

    scheduleAnnouncementRefresh() {
      if (!this.announcement) return;
      cancelAnimationFrame(this.marqueeResizeFrame);
      this.marqueeResizeFrame = requestAnimationFrame(() => {
        this.setupAnnouncementMarquee();
        this.updateStickyOffset();
      });
    }

    handleResize() {
      this.scheduleAnnouncementRefresh();
      this.updateStickyOffset();
    }

    updateStickyOffset() {
      if (!this.sectionWrapper) return;
      const offset = this.announcement?.offsetHeight || 0;
      this.sectionWrapper.style.setProperty('--rd-sticky-offset', `${offset}px`);
    }

    openDrawer(audience = 'women') {
      this.closeSmartSearch(false);
      if (!this.layer || !this.drawer) return;
      this.lastFocused = document.activeElement;
      this.layer.hidden = false;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rd-salty-drawer-open');
      this.activateAudience(audience, true);
      requestAnimationFrame(() => {
        this.layer.classList.add('is-open');
        this.drawer.querySelector('[data-drawer-close]')?.focus();
      });
    }

    closeDrawer(restoreFocus = true, onClosed = null) {
      if (!this.layer || this.layer.hidden || !this.drawer) {
        if (typeof onClosed === 'function') onClosed();
        return;
      }
      this.layer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rd-salty-drawer-open');

      const finish = () => {
        if (!this.layer.classList.contains('is-open')) {
          this.layer.hidden = true;
          if (typeof onClosed === 'function') onClosed();
        }
      };
      const duration = parseFloat(getComputedStyle(this.drawer).transitionDuration) || 0;
      if (duration === 0) finish();
      else this.drawer.addEventListener('transitionend', finish, { once: true });

      if (restoreFocus && this.lastFocused instanceof HTMLElement) this.lastFocused.focus();
    }

    openCartDrawer(event, trigger) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;

      event.preventDefault();
      const openNativeDrawer = () => {
        requestAnimationFrame(() => cartDrawer.open(trigger));
      };

      if (this.layer && !this.layer.hidden) {
        this.closeDrawer(false, openNativeDrawer);
      } else {
        openNativeDrawer();
      }
    }

    activateAudience(audience, openFirstPanel = false) {
      this.currentAudience = audience;
      this.root.querySelectorAll('[data-drawer-tab]').forEach((button) => {
        const active = button.dataset.drawerTab === audience;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      this.root.querySelectorAll('[data-audience]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.audience === audience);
      });
      const activeAudience = this.root.querySelector(`[data-audience="${audience}"]`);
      this.closePanels(activeAudience || this.root);
      if (openFirstPanel && activeAudience) {
        const firstTrigger = activeAudience.querySelector('[data-panel-trigger]');
        if (firstTrigger) this.openPanel(firstTrigger, false);
      }
    }

    openPanel(button, focusPanel = false) {
      const audience = button.closest('[data-audience]');
      if (!audience) return;
      const panelName = button.dataset.panelTrigger;
      audience.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== panelName;
      });
      audience.querySelectorAll('[data-panel-trigger]').forEach((trigger) => {
        const active = trigger === button;
        trigger.setAttribute('aria-expanded', String(active));
        trigger.closest('li')?.classList.toggle('is-active', active);
      });
      if (focusPanel) {
        const target = audience.querySelector(`[data-panel="${CSS.escape(panelName)}"]`);
        target?.querySelector('a,button')?.focus();
      }
    }

    closePanels(scope = this.root) {
      scope.querySelectorAll?.('[data-panel]').forEach((panel) => { panel.hidden = true; });
      scope.querySelectorAll?.('[data-panel-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.closest('li')?.classList.remove('is-active');
      });
    }

    toggleGenderMenu() {
      if (!this.genderMenu || !this.genderTrigger) return;
      const open = this.genderMenu.hidden;
      this.genderMenu.hidden = !open;
      this.genderTrigger.setAttribute('aria-expanded', String(open));
    }

    chooseGender(audience, label) {
      const current = this.root.querySelector('[data-current-gender]');
      if (current) current.textContent = label;
      if (this.genderMenu) this.genderMenu.hidden = true;
      this.genderTrigger?.setAttribute('aria-expanded', 'false');
      this.openDrawer(audience);
    }

    onDocumentClick(event) {
      if (this.searchOpen && this.smartSearch && !this.smartSearch.contains(event.target)) {
        this.closeSmartSearch(false);
      }

      if (this.genderMenu && !this.genderMenu.hidden
        && !this.genderMenu.contains(event.target)
        && !this.genderTrigger?.contains(event.target)) {
        this.genderMenu.hidden = true;
        this.genderTrigger?.setAttribute('aria-expanded', 'false');
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        if (this.searchOpen) {
          this.closeSmartSearch(true);
          return;
        }
        if (this.genderMenu && !this.genderMenu.hidden) {
          this.genderMenu.hidden = true;
          this.genderTrigger?.setAttribute('aria-expanded', 'false');
          this.genderTrigger?.focus();
          return;
        }
        this.closeDrawer();
      }

      if (event.key !== 'Tab' || !this.layer || this.layer.hidden || !this.drawer) return;
      const focusable = [...this.drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.closest('[hidden]') && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const currentY = Math.max(window.scrollY, 0);
        this.root.classList.toggle('is-scrolled', currentY > 8);
        if (this.root.dataset.stickyMode === 'on-scroll-up') {
          const movingDown = currentY > this.lastScrollY;
          this.root.classList.toggle('is-hidden', movingDown && currentY > this.root.offsetHeight + 30);
        }
        this.lastScrollY = currentY;
        this.ticking = false;
      });
    }

    destroy() {
      this.abortController.abort();
      cancelAnimationFrame(this.marqueeResizeFrame);
      window.clearTimeout(this.searchPlaceholderTimer);
      window.clearTimeout(this.searchDebounceTimer);
      this.searchRequestController?.abort();
      this.searchRequestController = null;
      this.searchSubmitController?.abort();
      this.searchSubmitController = null;
      this.searchForm?.removeAttribute('aria-busy');
      this.searchPlaceholderState = null;
      document.body.classList.remove('rd-smart-search-open');
      this.resizeObserver?.disconnect();
      this.sectionWrapper?.classList.remove('rd-salty-section-sticky');
      this.sectionWrapper?.style.removeProperty('--rd-sticky-offset');
      this.root.removeAttribute('data-rd-ready');
    }
  }

  const init = (scope = document) => {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new MirraiSaltyHeader(root));
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.(ROOT_SELECTOR)) roots.push(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();